import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
// import { OrderItem } from '@symphony-solo/domain/order/src/lib/models/';
// import { OrderFacade } from '@symphony-solo/domain/order/src/lib/+state/order.facade';
import { ChooseLocationFacade } from '@symphony-solo/domain/choose-location/src';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import {
  addCartItemSuccessAction,
  checkCartItemsAvailabilityAction,
  ClearStateAction,
  deleteCartItemSuccessAction,
  getCartAction,
  loadCartSuccessAction,
  setAccountID,
  setCartStartTimeAction,
  updateCartItemSuccessAction,
} from './cart.actions';
import { ICartFeature } from './cart.feature.state';
import { getCart, getFailures, getIsLoading, getCartUnAvailabilItems, getAccountId, getCartStartTime } from './cart.selectors';
import { concatMap, first, map } from 'rxjs/operators';
import { LocationPropTypes } from '@symphony-solo/domain/choose-location/src/lib/models';

@Injectable()
export class CartFacade {
  public isLoading$ = this.store.select(getIsLoading);
  public failures$ = this.store.select(getFailures);
  public cart$ = this.store.select(getCart);
  public accountID$ = this.store.select(getAccountId);
  public cartStartTime$ = this.store.select(getCartStartTime);
  public cartUnAvailabilItems$ = this.store.select(getCartUnAvailabilItems);

  public isCheckoutAllowed$ = this.store.select(getCartUnAvailabilItems).pipe(
    map((items) => {
      return items?.length > 0 ? false : true;
    })
  );

  public cartSubtotal = new BehaviorSubject<number>(0);
  public cartSubtotal$ = this.cartSubtotal.asObservable();
  public cartTotal = new BehaviorSubject<number>(0);
  public cartTotal$ = this.cartSubtotal.asObservable();

  public subTotal$ = this.cart$.pipe(
    map((cartItems) => {
      return cartItems.reduce((accumulator, currentValue) => {
        return +(accumulator + +this.currentItemTotal(currentValue)).toFixed(2);
      }, 0);
    })
  );

  public total$ = combineLatest([this.subTotal$]).pipe(
    concatMap(([cartSubtotal]) => {
      return of(cartSubtotal);
    })
  );

  public isMinimumOrderSpend$ = combineLatest([this.total$, this.chooseLocationFacade.locationPropTypes$]).pipe(
    concatMap(([total, locationPropTypes]) => {
      const minimumOrderSpend = locationPropTypes[LocationPropTypes.MinimumOrderSpend];
      if (minimumOrderSpend && minimumOrderSpend.value > 0) {
        return of(total >= +minimumOrderSpend.value);
      }
      return of(true);
    })
  );

  public minimumOrderSpendMsg$ = combineLatest([this.chooseLocationFacade.locationPropTypes$]).pipe(
    concatMap(([locationPropTypes]) => {
      const minimumOrderSpend = locationPropTypes[LocationPropTypes.MinimumOrderSpend];
      return of(`Minimum order amount is £${minimumOrderSpend.value}.`);
    })
  );

  private items: any;

  constructor(private store: Store<ICartFeature>, private chooseLocationFacade: ChooseLocationFacade) {}

  public getCart(): void {
    this.store.dispatch(getCartAction());
  }

  public loadCart(data: any): void {
    this.store.dispatch(loadCartSuccessAction(data));
    this.calculateSubtotal();
  }

  public removeItemFromCart(id: any): void {
    this.store.dispatch(deleteCartItemSuccessAction(id));
  }

  public checkItemInCart(item) {
    const existingItem = this.items?.find(
      (x) => x.productID === item.productID && x.productName === item.productName && x.total.price === item.total.price
    );
    return existingItem || null;
  }

  public addToCart(cartItem: any, cartItemIndex?): void {
    // const isExistItem = this.checkItemInCart(selectedProduct);
    // if(isExistItem) {
    //   const existingCart = { ...isExistItem };
    //   existingCart.quantity = Number(existingCart.quantity) + selectedProduct.quantity;
    //   this.onQuantityChange({cart: existingCart});
    // } else {

    this.store.dispatch(addCartItemSuccessAction({ cartItem, cartItemIndex }));
    // }
    setTimeout(() => {
      this.calculateSubtotal();
      this.calculateTotal();
    }, 100);
  }

  public calculateSubtotal(): void {
    this.cart$.pipe(first()).subscribe((x) => (this.items = x));
    this.cartSubtotal.next(
      this.items?.reduce((accumulator, currentValue) => {
        return +(accumulator + +this.currentItemTotal(currentValue)).toFixed(2);
      }, 0)
    );
  }

  public calculateTotal(totalDiscount?: number): void {
    if (totalDiscount === 0 || !totalDiscount) {
      this.cartTotal.next(this.cartSubtotal.value);
    } else if (this.cartSubtotal.value !== 0) {
      this.cartTotal.next(+(this.cartSubtotal.value - totalDiscount).toFixed(2));
    }
  }

  public currentItemTotal(item: any): number {
    const itemBasePrice = item.total.oPrice * item.quantity;

    let childItemsTotal = 0;

    for (const childItem of item.childItems) {
      if (+childItem.total.price) {
        childItemsTotal += +childItem.oPrice * +childItem.quantity;
      }
    }

    const itemPrice = itemBasePrice + childItemsTotal;
    return itemPrice;
  }

  public onQuantityChange(shoppingItem: any) {
    const price = shoppingItem.cart.total.oPrice * shoppingItem.cart.quantity;
    shoppingItem.cart.total = { ...shoppingItem.cart.total, price: +price.toFixed(2) };
    this.store.dispatch(updateCartItemSuccessAction(shoppingItem));
    this.calculateSubtotal();
  }

  public checkCartItemsAvailability(): void {
    this.store.dispatch(checkCartItemsAvailabilityAction());
  }

  public setAccountID(accountID: number): void {
    this.store.dispatch(setAccountID({ accountID }));
  }
  public setCartStartTime(): void {
    this.store.dispatch(setCartStartTimeAction());
  }

  public clearState(actionType: string): void {
    this.store.dispatch(ClearStateAction({ actionType }));
  }
}
